import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function PasswordSetupPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/setup-token/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 6) return setError("Şifre en az 6 karakter olmalıdır");
    if (pw1 !== pw2) return setError("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] text-slate-900 p-8">
      <div className="w-full max-w-sm fade-in">
        {invalid ? (
          <div className="text-center space-y-3" data-testid="setup-invalid">
            <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Bağlantı Geçersiz</h1>
            <p className="text-slate-500 text-sm">Bu şifre oluşturma bağlantısı geçersiz veya süresi dolmuş. Lütfen yöneticinizle iletişime geçin.</p>
          </div>
        ) : done ? (
          <div className="text-center space-y-3" data-testid="setup-success">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto" />
            <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Şifreniz Oluşturuldu</h1>
            <p className="text-slate-500 text-sm">Giriş sayfasına yönlendiriliyorsunuz...</p>
          </div>
        ) : !info ? (
          <p className="text-slate-500 text-sm text-center">Yükleniyor...</p>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">COP31 Tedarikçi Portalı</div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tighter mt-2">Şifrenizi Oluşturun</h1>
              <p className="text-sm text-slate-500 mt-2">
                Sayın {info.contact_name}, <span className="text-slate-900">{info.company_name}</span> hesabınız ({info.username}) için yeni şifre belirleyin.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw1">Yeni Şifre</Label>
              <div className="relative">
                <Input
                  id="pw1"
                  data-testid="setup-password-input"
                  type={show1 ? "text" : "password"}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="rounded-none bg-white border-slate-200 h-11 pr-11"
                  required
                />
                <button type="button" data-testid="setup-toggle-pw1" onClick={() => setShow1(!show1)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 transition-colors duration-200">
                  {show1 ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw2">Yeni Şifre (Tekrar)</Label>
              <div className="relative">
                <Input
                  id="pw2"
                  data-testid="setup-password-confirm-input"
                  type={show2 ? "text" : "password"}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="rounded-none bg-white border-slate-200 h-11 pr-11"
                  required
                />
                <button type="button" data-testid="setup-toggle-pw2" onClick={() => setShow2(!show2)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 transition-colors duration-200">
                  {show2 ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-red-500 text-sm" data-testid="setup-error">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy} data-testid="setup-submit-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
              {busy ? "Kaydediliyor..." : "Şifreyi Kaydet"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
